import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { RootState } from '../../store'

export type LiveFlashValueSelector = (state: RootState) => string

type LiveFlashValueProps = {
    selectText: LiveFlashValueSelector
    className?: string
    style?: React.CSSProperties
    /** How long the whole value stays colored before only changed digits remain. */
    fullFlashMs?: number
    /**
     * Minimum quiet gap since the previous value change before flash is shown.
     * Faster ticks skip the effect and keep the default text color.
     */
    minQuietMs?: number
    upColor?: string
    downColor?: string
}

const DEFAULT_UP = 'var(--green-text-color)'
const DEFAULT_DOWN = 'var(--red-text-color)'

/** First index where `prev` and `next` differ; `0` if lengths diverge before that. */
export const findFirstDiffIndex = (prev: string, next: string): number => {
    const len = Math.min(prev.length, next.length)
    for (let i = 0; i < len; i++) {
        if (prev.charCodeAt(i) !== next.charCodeAt(i)) return i
    }
    return len < next.length || len < prev.length ? len : -1
}

const parseDisplayNumber = (text: string): number => {
    if (!text) return NaN
    return Number(text.replace(/,/g, ''))
}

const LiveFlashValue = ({
    selectText,
    className,
    style,
    fullFlashMs = 400,
    minQuietMs = 500,
    upColor = DEFAULT_UP,
    downColor = DEFAULT_DOWN,
}: LiveFlashValueProps) => {
    const store = useStore<RootState>()
    const prefixRef = useRef<HTMLSpanElement>(null)
    const suffixRef = useRef<HTMLSpanElement>(null)
    const selectTextRef = useRef(selectText)
    const lastTextRef = useRef<string | null>(null)
    const lastChangeAtRef = useRef(0)
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const upColorRef = useRef(upColor)
    const downColorRef = useRef(downColor)
    const fullFlashMsRef = useRef(fullFlashMs)
    const minQuietMsRef = useRef(minQuietMs)

    selectTextRef.current = selectText
    upColorRef.current = upColor
    downColorRef.current = downColor
    fullFlashMsRef.current = fullFlashMs
    minQuietMsRef.current = minQuietMs

    useEffect(() => {
        const clearFlashTimer = () => {
            if (flashTimerRef.current != null) {
                clearTimeout(flashTimerRef.current)
                flashTimerRef.current = null
            }
        }

        const paint = (text: string, diffIndex: number, flashColor: string | null, fullFlash: boolean) => {
            const prefixEl = prefixRef.current
            const suffixEl = suffixRef.current
            if (!prefixEl || !suffixEl) return

            if (fullFlash || flashColor == null || diffIndex < 0) {
                prefixEl.textContent = text
                suffixEl.textContent = ''
                prefixEl.style.color = flashColor ?? ''
                suffixEl.style.color = ''
                return
            }

            prefixEl.textContent = text.slice(0, diffIndex)
            suffixEl.textContent = text.slice(diffIndex)
            prefixEl.style.color = ''
            suffixEl.style.color = flashColor
        }

        const apply = () => {
            const prefixEl = prefixRef.current
            const suffixEl = suffixRef.current
            if (!prefixEl || !suffixEl) return

            const text = selectTextRef.current(store.getState())
            const prev = lastTextRef.current

            if (prev === null) {
                lastTextRef.current = text
                lastChangeAtRef.current = performance.now()
                paint(text, -1, null, true)
                return
            }

            if (prev === text) return

            const now = performance.now()
            const quietEnough = now - lastChangeAtRef.current >= minQuietMsRef.current
            lastChangeAtRef.current = now
            lastTextRef.current = text
            clearFlashTimer()

            // Rapid ticks: update digits only, no flash color
            if (!quietEnough) {
                paint(text, -1, null, true)
                return
            }

            const prevNum = parseDisplayNumber(prev)
            const nextNum = parseDisplayNumber(text)
            let flashColor: string | null = null
            if (Number.isFinite(prevNum) && Number.isFinite(nextNum) && prevNum !== nextNum) {
                flashColor = nextNum > prevNum ? upColorRef.current : downColorRef.current
            }

            const diffIndex = findFirstDiffIndex(prev, text)

            if (flashColor == null || diffIndex < 0) {
                paint(text, -1, null, true)
                return
            }

            // Phase 1: whole value colored
            paint(text, 0, flashColor, true)

            // Phase 2: only changed suffix stays highlighted
            flashTimerRef.current = setTimeout(() => {
                flashTimerRef.current = null
                // Ignore if a newer tick already replaced the text
                if (lastTextRef.current !== text) return
                paint(text, diffIndex, flashColor, false)
            }, fullFlashMsRef.current)
        }

        apply()
        const unsubscribe = store.subscribe(apply)
        return () => {
            clearFlashTimer()
            unsubscribe()
        }
    }, [store])

    return (
        <span className={className} style={style}>
            <span ref={prefixRef} />
            <span ref={suffixRef} />
        </span>
    )
}

export default LiveFlashValue
